import _ from "lodash";
import {
  addChildToFamilyMemberMutation,
  createFeedbackMutation,
  makeTagEntitySeenMutation,
  markStudentAsRequestedForFamilyInviteMutation,
  markUserCommunicationAsSeenMutation,
  markStudentRequestStateSeenMutation,
  updateCourseBookmarkMutation,
} from "./CoursesMutations";
import client from "lib/apolloClient/client";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getChildrenQuery } from "modules/CommonQuery";
import { getOrganizationDetailsUnitPlanCount } from "Courses/modules/CoursesFragments";
import { getBasicDeviceInfo } from "Utils";
import {
  getSingleCourseV2FromCache,
  writeSingleCourseV2IntoCache,
} from "./CoursePaginationHelpers";
import update from "immutability-helper";
import { getAcademicYearId } from "modules/CommonSelectors";
import { getWorkrampLoginUrlQuery } from "./CoursePaginationQuery";
import { setFullScreenLoading } from "Platform/modules/PlatformModule";
import { getPlatformUserDetailsQuery } from "ChildrenPage/modules/ChildrenPageQuery";

export const NAME = "courses";
export const UPDATE_PENDING_INVITES_BANNER_STATE = `UPDATE_PENDING_INVITES_BANNER_STATE ${NAME}`;
export const UPDATE_SELECTED_CHILD = "UPDATE_SELECTED_CHILD";
export const SET_COURSES_FILTERS = "SET_COURSES_FILTERS";
export const UPDATE_LTI_TOOL_LIST = "UPDATE_LTI_TOOL_LIST";

export const addChild = ({
  studentCode,
  userId,
  signInFlow,
  setErrorMessage,
  refetchQueries = [],
  includePlatformUserQuery = false,
  logId,
}) => {
  return async function (dispatch, getState) {
    const platformUserQuery = {
      query: getPlatformUserDetailsQuery,
      variables: {
        id: userId,
        type: "FAMILY_MEMBER",
        portalType: "PLANNER",
      },
    };

    const finalQueryList = includePlatformUserQuery
      ? [...refetchQueries, platformUserQuery]
      : refetchQueries;

    try {
      const res = await client.mutate({
        mutation: addChildToFamilyMemberMutation,
        variables: { inviteCode: studentCode, id: userId, logId },
        refetchQueries: _.size(refetchQueries)
          ? finalQueryList
          : !signInFlow
          ? [platformUserQuery]
          : [],
        awaitRefetchQueries: true,
      });
      return _.get(res, "data.platform.addChildToFamilyMember", {});
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        const error = _.get(e, "graphQLErrors[0].message", "");
        if (setErrorMessage) {
          setErrorMessage(error);
        } else {
          dispatch(setToastMsg({ msg: error, notString: true }));
        }
      }
    }
  };
};

export const createFeedback = ({ email, description, attachments }) => {
  return async function (dispatch, getState) {
    const deviceInfo = getBasicDeviceInfo();
    const metadata = {
      includedInReport: deviceInfo,
      ...deviceInfo,
    };

    try {
      await client.mutate({
        mutation: createFeedbackMutation,
        variables: {
          input: { email, description, attachments, metadata },
        },
      });

      return { successfull: true };
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg({ msg: "common:something_went_wrong_error" }));
      }
      return { successfull: false };
    }
  };
};

export const resetMBLibraryNewCount = ({ regions }) => {
  return async function (dispatch, getState) {
    try {
      const state = getState();
      const organizationId = state.login.userInfo.org_id;
      const res = await client.mutate({
        mutation: makeTagEntitySeenMutation,
        variables: {
          input: {
            regions,
            entityType: "UNITPLAN",
          },
          portalType: "PLANNER",
        },
        refetchQueries: [
          {
            query: getOrganizationDetailsUnitPlanCount,
            variables: {
              id: organizationId,
              regions,
              portalType: "PLANNER",
            },
          },
        ],
      });
      return res;
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      }
    }
  };
};

export const setUserMessageToSeen = ({ messageType }) => {
  return async function (dispatch, getState) {
    try {
      const res = await client.mutate({
        mutation: markUserCommunicationAsSeenMutation,
        variables: {
          input: {
            messageType,
            appType: "WEB",
          },
        },
      });
      return res;
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      }
    }
  };
};

export const getCommonCourseUnitPlansCountFromCache = ({ id, regions }) => {
  try {
    const queryData = client.readQuery({
      query: getOrganizationDetailsUnitPlanCount,
      variables: {
        id,
        regions,
        portalType: "PLANNER",
      },
    });
    if (queryData != null) {
      return _.get(queryData, "node", {});
    } else {
      return {};
    }
  } catch (e) {
    return {};
  }
};

export const markStudentAsRequestedForFamilyInvite = ({
  childIds,
  courseId,
  learningCourseId = "",
}) => {
  return async function (dispatch, getState) {
    const userInfo = getUserInfo();
    const userId = userInfo.id;

    try {
      const res = await client.mutate({
        mutation: markStudentAsRequestedForFamilyInviteMutation,
        variables: {
          input: {
            childIds,
            courseId,
            learningCourseId,
          },
        },
        refetchQueries: [
          {
            query: getChildrenQuery,
            variables: {
              id: userId,
              type: "FAMILY_MEMBER",
            },
          },
        ],
        awaitRefetchQueries: true,
      });
      return _.get(res, "data.platform.addChildToFamilyMember", {});
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg({ msg: "common:something_went_wrong_error" }));
      }
    }
  };
};

export const markStudentRequestStateSeen = (childIds = []) => {
  return async function (dispatch, getState) {
    try {
      await client.mutate({
        mutation: markStudentRequestStateSeenMutation,
        variables: {
          input: {
            childIds,
          },
        },
      });
      return true;
    } catch (e) {
      console.error(e);
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg({ msg: "common:something_went_wrong_error" }));
      }
    }
  };
};

export const updateBookmarkCourse = (courseIdInput, refetchCourseQuery) => {
  return async (dispatch, getState) => {
    const isBookmarkClicked = _.has(courseIdInput, "addCourseIds");
    const state = getState();
    const selectedAcademicYearId = getAcademicYearId(state);
    const courseGradeFilters = { academicYearId: selectedAcademicYearId };
    const courseId = isBookmarkClicked
      ? _.get(courseIdInput, "addCourseIds", [])
      : _.get(courseIdInput, "removeCourseIds", []);
    const oldCourse = getSingleCourseV2FromCache({
      courseId: courseId[0],
      courseGradeFilters,
    });
    try {
      const isUpdateDone = await client.mutate({
        mutation: updateCourseBookmarkMutation,
        variables: {
          input: courseIdInput,
        },
        // refetchQueries: refetchCourseQuery,
        optimisticResponse: {
          __typename: "Mutation",
          platform: {
            __typename: "PlatformMutations",
            updateCourseAsBookmark: true,
          },
        },
        update: (
          cache,
          {
            data: {
              platform: { updateCourseAsBookmark },
            },
          }
        ) => {
          if (updateCourseAsBookmark) {
            setTimeout(() => {
              writeSingleCourseV2IntoCache({
                courseId,
                courseGradeFilters,
                data: {
                  ...oldCourse,
                  isBookmark: isBookmarkClicked,
                },
              });
            });
          }
        },
      });
      return isUpdateDone;
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const getWorkrampLoginUrl = ({ id, type }) => {
  return async dispatch => {
    try {
      dispatch(setFullScreenLoading(true));
      const response = await client.query({
        query: getWorkrampLoginUrlQuery,
        variables: {
          id,
          type,
        },
        fetchPolicy: "no-cache",
      });
      dispatch(setFullScreenLoading(false));
      return _.get(response, "data.node.workrampLoginUrl", "");
    } catch (error) {
      console.error(error);
      return "";
    }
  };
};

export const updateSelectedChild = child => {
  return {
    type: UPDATE_SELECTED_CHILD,
    data: child,
  };
};

const initialState = {
  showPendingInvitesBanner: true,
  showFullScreenLoader: false,
  selectedChild: {},
  filters: {},
  ltiToolList: [],
};

export const updatePendingInvitesBanner = data => {
  return {
    type: UPDATE_PENDING_INVITES_BANNER_STATE,
    data,
  };
};

export const updateCoursesFiltersInRedux = data => {
  return {
    type: SET_COURSES_FILTERS,
    data,
  };
};

export const updateLtiToolList = data => {
  return {
    type: UPDATE_LTI_TOOL_LIST,
    data,
  };
};

const REDUCER_HANDLERS = {
  [UPDATE_PENDING_INVITES_BANNER_STATE]: (state, action) => {
    const bannerState = action.data;
    return update(state, {
      showPendingInvitesBanner: {
        $set: bannerState,
      },
    });
  },
  [UPDATE_SELECTED_CHILD]: (state, action) => {
    const selectedChild = action.data;
    return update(state, {
      selectedChild: {
        $set: selectedChild,
      },
    });
  },
  [SET_COURSES_FILTERS]: (state, action) => {
    return update(state, {
      filters: {
        $set: action.data,
      },
    });
  },
  [UPDATE_LTI_TOOL_LIST]: (state, action) => {
    return update(state, {
      ltiToolList: { $set: action.data },
    });
  },
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
